// src/App.js
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { flushSync } from 'react-dom';
import { BrowserRouter as Router, Route, Routes, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Typography, Box } from '@mui/material';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
// Redux Provider removed - not being used
import {
  getUserBackgroundPreference,
  setUserBackgroundPreference,
  getUserStartupTabPreference,
  setUserStartupTabPreference,
  getUserLanguagePreference,
  setUserLanguagePreference,
  getUserOnboardingStatus,
  setUserOnboardingStatus,
  getUserWallpaperPreference,
  setUserWallpaperPreference,
  getUserInterfaceStyle,
  getUserProfile
} from './components/AGTHall/services/AGTHallService';
import { useMediaQuery } from '@mui/material';
import { getTheme, cssVariables } from './theme';
import loadingLogoWhite from './assets/images/loading-white.svg';
import loadingLogoBlack from './assets/images/loading-black.svg';
import { ProProvider, useProStatus } from './context/ProContext';
// Import IntercomProvider and IntercomHider
import IntercomProvider from './components/common/IntercomProvider';
import IntercomHider from './components/common/IntercomHider';
// Import i18n
import { useTranslation } from 'react-i18next';

import LimitedDashboard from './components/AGTHall/dashboard/LimitedDashboard';
import FullDashboard from './components/AGTHall/dashboard/FullDashboard/FullDashboard';
import UserProfile from './components/AGTHall/auth/UserProfile';
import SubscriptionSuccess from './components/AGTUps/Subscription/SubscriptionSuccess';
import AppOnboarding from './components/AGTHall/onboarding/AppOnboarding';

// Importamos el ProductProvider para disponer del contexto global de productos
import { ProductProvider } from './context/ProductContext';

// Wallpaper images are in public folder, not imported
const wallpaperDay = '/images/wallpaper/wallpaperday-appartment.png';
const wallpaperNight = '/images/wallpaper/wallpapernight-appartment.png';
const EMPTY_ONBOARDING_DATA = {
  birthday: null,
  goals: [],
  goalDetail: null,
  stepsViewed: [],
  lastStepCompleted: null,
  currentStepId: null
};

function AppContent() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setErrorMessage] = useState(null);
  const [backgroundType, setBackgroundType] = useState('system');
  const [startupTabPreference, setStartupTabPreference] = useState('default');
  const [activeSection, setActiveSection] = useState('home');
  const [language, setLanguage] = useState('auto');
  const [wallpaperPreference, setWallpaperPreference] = useState('appartment');
  const [interfaceStyle, setInterfaceStyle] = useState('classic');
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [isUpdatingOnboarding, setIsUpdatingOnboarding] = useState(false);
  const [onboardingData, setOnboardingData] = useState(EMPTY_ONBOARDING_DATA);
  const navigate = useNavigate();
  const location = useLocation();
  const auth = getAuth();
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  const { checkStatus, isPro } = useProStatus();
  const { i18n } = useTranslation();

  const theme = useMemo(() => {
    let currentTheme = backgroundType;

    // Normalizar solo el modo 'system', mantener wallpaper modes
    if (backgroundType === 'system') {
      currentTheme = prefersDarkMode ? 'dark' : 'light';
    } else if (backgroundType === 'wallpaper' ||
               backgroundType === 'wallpaper-light' ||
               backgroundType === 'wallpaper-dark') {
      // Mantener wallpaper para que getTheme() pueda establecer actualMode correctamente
      currentTheme = 'wallpaper';
    }

    return createTheme(getTheme(currentTheme));
  }, [backgroundType, prefersDarkMode]);

  const enrichedUser = useMemo(() => {
    if (!user) return null;
    return {
      ...user,
      isPro
    };
  }, [user, isPro]);

  useEffect(() => {
    const checkProStatus = async () => {
      if (user?.uid) {
        try {
          await checkStatus(user.uid);
        } catch (error) {
          console.error('Error checking pro status:', error);
        }
      }
    };

    checkProStatus();
  }, [user?.uid, checkStatus]);

  // Effect for theme changes
  useEffect(() => {
    let currentTheme = backgroundType;

    if (backgroundType === 'system') {
      currentTheme = prefersDarkMode ? 'dark' : 'light';
    }

    document.documentElement.setAttribute('data-theme', currentTheme);

    const style = document.createElement('style');
    style.innerHTML = cssVariables;
    document.head.appendChild(style);

    return () => document.head.removeChild(style);
  }, [backgroundType, prefersDarkMode]);

  useEffect(() => {
    let timeout;

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      try {
        if (currentUser) {
          // Cargar TODAS las preferencias en paralelo para ser más rápido
          const [
            preference,
            tabPreference,
            langPreference,
            wallpaperPref,
            interfaceStylePref,
            onboardingPreference,
            userProfileData
          ] = await Promise.all([
            getUserBackgroundPreference(currentUser.uid),
            getUserStartupTabPreference(currentUser.uid),
            getUserLanguagePreference(currentUser.uid),
            getUserWallpaperPreference(currentUser.uid),
            getUserInterfaceStyle(currentUser.uid),
            getUserOnboardingStatus(currentUser.uid),
            getUserProfile(currentUser.uid)
          ]);

          // Combinar datos de Firebase Auth con datos de DynamoDB (perfil)
          const enrichedUser = {
            ...currentUser,
            photoURL: userProfileData?.profile?.photoURL || currentUser.photoURL,
            displayName: userProfileData?.profile?.displayName || currentUser.displayName
          };

          setUser(enrichedUser);

          // Aplicar las preferencias cargadas
          setBackgroundType(preference === null ? 'system' : preference);
          setStartupTabPreference(tabPreference);
          setLanguage(langPreference || 'auto');
          setWallpaperPreference(wallpaperPref || 'appartment');
          setInterfaceStyle(interfaceStylePref || 'classic');
          setOnboardingStatus(onboardingPreference || 'pending');
          setOnboardingData(EMPTY_ONBOARDING_DATA);

          // Aplicar el idioma
          if (langPreference === 'auto') {
            const browserLang = navigator.language.split('-')[0];
            i18n.changeLanguage(browserLang);
          } else {
            i18n.changeLanguage(langPreference);
          }

          // Aplicar la preferencia de pestaña si no es 'default'
          if (tabPreference !== 'default') {
            setActiveSection(tabPreference);
          }
        } else {
          setUser(null);
          setBackgroundType('system');
          setStartupTabPreference('default');
          setLanguage('auto');
          setWallpaperPreference('appartment');
          setInterfaceStyle('classic');
          setOnboardingStatus(null);
          setOnboardingData(EMPTY_ONBOARDING_DATA);
          // Si no hay usuario, usar el idioma del navegador
          const browserLang = navigator.language.split('-')[0];
          i18n.changeLanguage(browserLang);
        }
      } catch (error) {
        console.error('Error during authentication or preference loading:', error);
        setErrorMessage('An error occurred while setting up the application. Please try again.');
      } finally {
        setLoading(false);
        clearTimeout(timeout);
      }
    });

    timeout = setTimeout(() => {
      setLoading(false);
      setErrorMessage('Authentication timed out. Please check your connection and try again.');
    }, 10000);

    return () => {
      unsubscribe();
      clearTimeout(timeout);
    };
  }, [auth, i18n]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const token = searchParams.get('token');
    if (location.pathname === '/auth/success' && token) {
      localStorage.setItem('token', token);
      navigate('/');
    }
  }, [location, navigate]);

  useEffect(() => {
    if (!user?.uid) return;

    let isActive = true;
    const refreshIntervalMs = 6 * 60 * 60 * 1000;

    const refreshUserProfile = async () => {
      try {
        const userProfileData = await getUserProfile(user.uid);
        if (!isActive || !userProfileData?.profile) return;

        const nextPhotoURL = userProfileData.profile.photoURL || user.photoURL;
        const nextDisplayName = userProfileData.profile.displayName || user.displayName;

        setUser((prev) => {
          if (!prev) return prev;
          if (prev.photoURL === nextPhotoURL && prev.displayName === nextDisplayName) {
            return prev;
          }
          return {
            ...prev,
            photoURL: nextPhotoURL,
            displayName: nextDisplayName
          };
        });
      } catch (error) {
        console.warn('Error refreshing user profile:', error);
      }
    };

    refreshUserProfile();
    const refreshInterval = setInterval(refreshUserProfile, refreshIntervalMs);

    return () => {
      isActive = false;
      clearInterval(refreshInterval);
    };
  }, [user?.uid, user?.photoURL, user?.displayName]);

  const handleLogin = useCallback((user) => {
    setUser(user);
    
    // Usar la preferencia de pestaña de inicio si existe
    if (startupTabPreference !== 'default') {
      setActiveSection(startupTabPreference);
    } else {
      setActiveSection('home');
    }
    
    navigate('/');
  }, [navigate, startupTabPreference]);

  const handleLogout = useCallback(() => {
    auth.signOut().then(() => {
      setUser(null);
      setBackgroundType('system');
      setStartupTabPreference('default');
      setLanguage('auto');
      setActiveSection('home');
      setOnboardingStatus(null);
      setOnboardingData(EMPTY_ONBOARDING_DATA);
      localStorage.removeItem('token');
      navigate('/');
    }).catch((error) => {
      console.error('Error al cerrar sesión:', error);
    });
  }, [auth, navigate]);

  const handleOnboardingBirthdayChange = useCallback((birthday) => {
    const normalizedBirthday =
      birthday instanceof Date && !Number.isNaN(birthday.getTime())
        ? birthday.toISOString()
        : null;

    setOnboardingData((prev) => ({
      ...prev,
      birthday: normalizedBirthday
    }));
  }, []);

  const handleOnboardingGoalChange = useCallback((goals) => {
    const normalizedGoals = Array.isArray(goals)
      ? [...new Set(goals.filter((goal) => typeof goal === 'string' && goal.trim().length > 0))]
      : [];

    setOnboardingData((prev) => ({
      ...prev,
      goals: normalizedGoals
    }));
  }, []);

  const handleOnboardingGoalDetailChange = useCallback((goalDetail) => {
    if (!goalDetail || typeof goalDetail !== 'object') {
      setOnboardingData((prev) => ({
        ...prev,
        goalDetail: null
      }));
      return;
    }

    const normalizedGoalDetail = {
      mode: goalDetail.mode || null,
      focusAreas: Array.isArray(goalDetail.focusAreas) ? goalDetail.focusAreas : [],
      exploration: goalDetail.exploration || null,
      other: goalDetail.other || null,
      otherText: typeof goalDetail.otherText === 'string' ? goalDetail.otherText.trim() : ''
    };

    setOnboardingData((prev) => ({
      ...prev,
      goalDetail: normalizedGoalDetail
    }));
  }, []);

  const handleOnboardingStepChange = useCallback((stepInfo) => {
    setOnboardingData((prev) => {
      const updatedStepsViewed = prev.stepsViewed.includes(stepInfo.stepId)
        ? prev.stepsViewed
        : [...prev.stepsViewed, stepInfo.stepId];

      return {
        ...prev,
        stepsViewed: updatedStepsViewed,
        lastStepCompleted: stepInfo.stepIndex,
        currentStepId: stepInfo.stepId
      };
    });
  }, []);

  const handleOnboardingStatusUpdate = useCallback(async (status, skipInfo = null) => {
    if (!user?.uid) return;
    setIsUpdatingOnboarding(true);
    try {
      const hasOnboardingDetails = Boolean(
        onboardingData.birthday ||
        onboardingData.goals.length > 0 ||
        onboardingData.goalDetail
      );

      let onboardingPayload = hasOnboardingDetails || onboardingData.stepsViewed.length > 0
        ? {
            ...onboardingData,
            capturedAt: new Date().toISOString()
          }
        : null;

      // Si el usuario hizo skip, agregar información del paso donde lo hizo
      if (status === 'skipped' && skipInfo) {
        onboardingPayload = {
          ...(onboardingPayload || onboardingData),
          skippedAt: skipInfo.stepId,
          skippedAtIndex: skipInfo.stepIndex,
          capturedAt: new Date().toISOString()
        };
      }

      await setUserOnboardingStatus(user.uid, status, onboardingPayload);
      setOnboardingStatus(status);
    } catch (error) {
      console.error('Error updating onboarding status:', error);
    } finally {
      setIsUpdatingOnboarding(false);
    }
  }, [user?.uid, onboardingData]);

  const handleBackgroundChange = useCallback(async (newBackgroundType) => {
    if (user) {
      try {
        await setUserBackgroundPreference(user.uid, newBackgroundType);
        setBackgroundType(newBackgroundType);
        let currentTheme = newBackgroundType;

        if (newBackgroundType === 'system') {
          currentTheme = prefersDarkMode ? 'dark' : 'light';
        }

        document.documentElement.setAttribute('data-theme', currentTheme);
      } catch (error) {
        console.error('Error setting user background preference:', error);
      }
    } else {
      setBackgroundType(newBackgroundType);
      let currentTheme = newBackgroundType;

      if (newBackgroundType === 'system') {
        currentTheme = prefersDarkMode ? 'dark' : 'light';
      }

      document.documentElement.setAttribute('data-theme', currentTheme);
    }
  }, [user, prefersDarkMode]);

  const handleStartupTabChange = useCallback(async (newStartupTab) => {
    if (user) {
      try {
        await setUserStartupTabPreference(user.uid, newStartupTab);
        setStartupTabPreference(newStartupTab);
        
        // Si el usuario selecciona una pestaña específica, aplicarla inmediatamente
        if (newStartupTab !== 'default') {
          setActiveSection(newStartupTab);
          localStorage.setItem('activeSection', newStartupTab);
        }
      } catch (error) {
        console.error('Error setting startup tab preference:', error);
      }
    }
  }, [user]);

  const handleLanguageChange = useCallback(async (newLanguage) => {
    if (user) {
      try {
        await setUserLanguagePreference(user.uid, newLanguage);
        setLanguage(newLanguage);
        
        // Aplicar el cambio de idioma
        if (newLanguage === 'auto') {
          const browserLang = navigator.language.split('-')[0];
          i18n.changeLanguage(browserLang);
        } else {
          i18n.changeLanguage(newLanguage);
        }
      } catch (error) {
        console.error('Error setting language preference:', error);
      }
    } else {
      setLanguage(newLanguage);
      // Aplicar el cambio de idioma
      if (newLanguage === 'auto') {
        const browserLang = navigator.language.split('-')[0];
        i18n.changeLanguage(browserLang);
      } else {
        i18n.changeLanguage(newLanguage);
      }
    }
  }, [user, i18n]);

  const handleWallpaperChange = useCallback(async (newWallpaper) => {
    if (user) {
      try {
        await setUserWallpaperPreference(user.uid, newWallpaper);
        setWallpaperPreference(newWallpaper);
      } catch (error) {
        console.error('Error setting wallpaper preference:', error);
      }
    } else {
      setWallpaperPreference(newWallpaper);
    }
  }, [user]);

  // Callback cuando el asistente actualiza las preferencias del usuario
  const handleUserPreferencesChanged = useCallback((updatedPreferences) => {
    console.log('🔔 [App.js] Preferencias actualizadas por el asistente:', updatedPreferences);

    // Actualizar backgroundType si cambió
    if (updatedPreferences.backgroundType && updatedPreferences.backgroundType !== backgroundType) {
      console.log(`  📱 Actualizando theme: ${backgroundType} → ${updatedPreferences.backgroundType}`);
      setBackgroundType(updatedPreferences.backgroundType);
    }

    // Actualizar idioma si cambió
    if (updatedPreferences.language && updatedPreferences.language !== language) {
      console.log(`  🌐 Actualizando idioma: ${language} → ${updatedPreferences.language}`);
      setLanguage(updatedPreferences.language);
      if (updatedPreferences.language === 'auto') {
        const browserLang = navigator.language.split('-')[0];
        i18n.changeLanguage(browserLang);
      } else {
        i18n.changeLanguage(updatedPreferences.language);
      }
    }

    // Actualizar startup tab si cambió
    if (updatedPreferences.startupTab && updatedPreferences.startupTab !== startupTabPreference) {
      console.log(`  📍 Actualizando startup tab: ${startupTabPreference} → ${updatedPreferences.startupTab}`);
      setStartupTabPreference(updatedPreferences.startupTab);
    }

    // TODO: Actualizar otras preferencias (wallpaper, selectedApps, etc.) si es necesario
  }, [backgroundType, language, startupTabPreference, i18n]);

  const handleNavigate = useCallback((section) => {
    console.log('[App.js] handleNavigate a sección:', section);
    setActiveSection(section);
    localStorage.setItem('activeSection', section);
    // NO usar navigate - todo en la misma ruta /
  }, []);

  const [initialChatMessage, setInitialChatMessage] = useState(null);

  const handleSendMessage = useCallback((message, shouldTransition = false) => {
    console.log('[App.js] handleSendMessage llamado con mensaje:', message);

    // CRÍTICO: Limpiar el currentChatId guardado ANTES de cambiar a chat
    // Esto evita que useChat intente restaurar el chat anterior cuando hay un mensaje inicial pendiente
    localStorage.removeItem('currentChatId');

    // Usar flushSync para forzar actualización síncrona INMEDIATA
    flushSync(() => {
      setActiveSection('chat');
      localStorage.setItem('activeSection', 'chat');
    });

    // Después de que el DOM se haya actualizado, guardar el mensaje
    setInitialChatMessage(message);

    console.log('[App.js] Cambio a chat completado (sin navigate)');
  }, []);

  const handleClearInitialMessage = useCallback(() => {
    setInitialChatMessage(null);
  }, []);

  // Determinar el tema actual para el wallpaper
  const currentTheme = useMemo(() => {
    if (backgroundType === 'system') {
      return prefersDarkMode ? 'dark' : 'light';
    }
    return backgroundType;
  }, [backgroundType, prefersDarkMode]);

  // Determinar la imagen de wallpaper según la hora del día
  const wallpaperImage = useMemo(() => {
    const hour = new Date().getHours();
    const isDayTime = hour >= 6 && hour < 18;
    return isDayTime ? wallpaperDay : wallpaperNight;
  }, []);

  const isOnboardingRequired = Boolean(user) &&
    onboardingStatus !== 'completed' &&
    onboardingStatus !== 'skipped';

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          width: '100vw',
          position: 'fixed',
          top: 0,
          left: 0,
          backgroundColor: theme.palette.colors.bg.primaryElevated,
          flexDirection: 'column',
          gap: '20px'
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <Box
            component="img"
            src={theme.palette.mode === 'light' ? loadingLogoBlack : loadingLogoWhite}
            sx={{
              width: 128,
              height: 128,
              animation: 'fadeIn 0.5s ease-in-out',
              '@keyframes fadeIn': {
                '0%': {
                  opacity: 0,
                },
                '100%': {
                  opacity: 1,
                },
              },
            }}
            alt="Loading"
          />
        </Box>
        
        <Box
          sx={{
            width: '200px',
            position: 'relative',
            height: '2px',
            backgroundColor: theme.palette.mode === 'light' 
              ? 'rgba(0, 0, 0, 0.1)' 
              : 'rgba(255, 255, 255, 0.1)',
            borderRadius: '4px',
            overflow: 'hidden'
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              height: '100%',
              backgroundColor: theme.palette.mode === 'light' 
                ? 'rgba(0, 0, 0, 0.6)' 
                : 'rgba(255, 255, 255, 0.6)',
              animation: 'progressLoad 2.5s ease-out forwards',
              '@keyframes progressLoad': {
                '0%': {
                  width: '0%',
                },
                '20%': {
                  width: '20%',
                },
                '40%': {
                  width: '40%',
                },
                '60%': {
                  width: '60%',
                },
                '80%': {
                  width: '80%',
                },
                '95%': {
                  width: '95%',
                },
                '100%': {
                  width: '100%',
                }
              }
            }}
          />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          width: '100vw',
          position: 'fixed',
          top: 0,
          left: 0,
          padding: 2,
          textAlign: 'center',
          backgroundColor: theme.palette.colors.bg.primaryElevated,
        }}
      >
        <Typography
          color="error"
          variant="h6"
          sx={{
            color: theme.palette.colors.label.primary,
          }}
        >
          {error}
        </Typography>
      </Box>
    );
  }

  if (isOnboardingRequired) {
    return (
      <ThemeProvider theme={theme}>
        <IntercomProvider user={enrichedUser} />
        <IntercomHider />

        <Box
          sx={{
            display: 'flex',
            minHeight: '100vh',
            backgroundColor: theme.palette.colors.bg.primaryElevated,
            color: theme.palette.colors.label.primary,
            transition: 'background-color 0.3s ease',
            ...(currentTheme === 'wallpaper' && {
              backgroundImage: `url(${wallpaperImage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              backgroundAttachment: 'fixed',
            }),
          }}
        >
          <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
            <AppOnboarding
              onComplete={() => handleOnboardingStatusUpdate('completed')}
              onSkip={(skipInfo) => handleOnboardingStatusUpdate('skipped', skipInfo)}
              isSubmitting={isUpdatingOnboarding}
              onBirthdayChange={handleOnboardingBirthdayChange}
              onGoalChange={handleOnboardingGoalChange}
              onGoalDetailChange={handleOnboardingGoalDetailChange}
              onStepChange={handleOnboardingStepChange}
              backgroundType={backgroundType}
              wallpaperPreference={wallpaperPreference}
              onBackgroundChange={handleBackgroundChange}
              onWallpaperChange={handleWallpaperChange}
              prefersDarkMode={prefersDarkMode}
            />
          </Box>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <IntercomProvider user={enrichedUser} />
      <IntercomHider />

      <Box
        sx={{
          display: 'flex',
          minHeight: '100vh',
          backgroundColor: theme.palette.colors.bg.primaryElevated,
          color: theme.palette.colors.label.primary,
          transition: 'background-color 0.3s ease',
          ...(currentTheme === 'wallpaper' && {
            backgroundImage: `url(${wallpaperImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundAttachment: 'fixed',
          }),
        }}
      >
        <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
          <Routes>
            <Route
              path="/"
              element={
                user ? (
                  <FullDashboard
                  user={enrichedUser}
                  onLogout={handleLogout}
                  backgroundType={backgroundType}
                  onBackgroundChange={handleBackgroundChange}
                  startupTabPreference={startupTabPreference}
                  onStartupTabChange={handleStartupTabChange}
                  language={language}
                  onLanguageChange={handleLanguageChange}
                  activeSection={activeSection}
                  onNavigate={handleNavigate}
                  onUserPreferencesChanged={handleUserPreferencesChanged}
                  onSendMessage={handleSendMessage}
                  initialChatMessage={initialChatMessage}
                  onClearInitialMessage={handleClearInitialMessage}
                  wallpaperPreference={wallpaperPreference}
                  interfaceStyle={interfaceStyle}
                />
              ) : (
                <LimitedDashboard
                  onLogin={handleLogin}
                  backgroundType={backgroundType}
                  onBackgroundChange={handleBackgroundChange}
                  language={language}
                  onLanguageChange={handleLanguageChange}
                />
              )
            }
          />
          <Route
            path="/profile"
            element={
              user ? (
                <UserProfile
                  user={enrichedUser}
                  onLogout={handleLogout}
                  backgroundType={backgroundType}
                  onBackgroundChange={handleBackgroundChange}
                  language={language}
                  onLanguageChange={handleLanguageChange}
                />
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route path="/auth/success" element={<Navigate to="/" />} />
          <Route
            path="/subscription/success"
            element={<SubscriptionSuccess />}
          />
        </Routes>
        </Box>
      </Box>
    </ThemeProvider>
  );
}

function App() {
  return (
    <ProProvider>
      <ProductProvider>
        <Router
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <CssBaseline />
          <AppContent />
        </Router>
      </ProductProvider>
    </ProProvider>
  );
}

export default App;
